import { fleetApi, FleetApiTypes } from 'api'
import { breadcrumbs } from 'constants/breadcrumbs'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from 'redux/hooks'
import {
  isMigratedUserSelector,
  isOperatorSelector,
} from 'redux/login/selectors'
import { MENU_NAMES } from 'texts/menuNames'
import { Button } from 'ui/atoms/Button'
import { PlusIcon } from 'ui/icons'
import { Confirmation } from 'ui/molecules/Confirmation'
import { TableTooltip } from 'ui/molecules/TableTooltip'
import { Table } from 'ui/molecules/Table'
import { Equipment } from './types'
import {
  EQUIPMENTS,
  EQUIPMENTS_HEAD_ITEMS,
  EQUIPMENT_DELETE_CONFIRMATION,
} from 'texts/equipments'
import { useDispatch } from 'react-redux'
import { setNotificationMessage } from 'redux/notifications/slice'
import { Container } from 'ui/molecules/Container'
import { PAGINATION_DROPDOWN_LABEL } from 'texts/uiTexts'
import { useApiRequest } from 'hooks/useApiRequest'
import { GTM } from 'utils/gtm'
import { DATA_LAYER_EVENT } from 'constants/dataLayerEvents'
import { useCompanyId } from 'hooks/useCompanyId'
import styles from './styles.module.scss'
import { prepareFiltersForReq, prepareSortingForReq } from 'utils/table'
import { EQUIPMENTS_FILTER_KEY } from 'constants/filters'
import { sortingSelector } from 'redux/sorting/selectors'
import {
  filterItemsSelector,
  selectedFiltersCountSelector,
  selectedFiltersSelector,
} from 'redux/filters/selectors'
import { useFilterSettings } from 'hooks/useFilterSettings'
import { setFilterItems } from 'redux/filters/slice'
import { prepareFilterItems } from 'utils/filters'
import { TableHeadProps } from 'ui/molecules/Table/TableHead/types'
import { EQUIPMENT_INDEX, MAX_PRICE_INDEX, PRICE_INDEX } from './constants'
import { ResetFiltersButton } from 'ui/components/ResetFiltersButton'
import { Pagination } from 'ui/molecules/Pagination'
import { usePagination } from 'hooks/usePagination'
import { MissingFieldsBadge } from 'ui/components/MissingFieldsBadge'
import warningIcon from 'assets/icons/yellow700/yellow700_warning_20x20.webp'
import { LazyImage, Typography, Checkbox } from '@frontend/design_system'
import { useCompanyInfo } from 'ui/hooks/useCompanyInfo'

export const Equipments = () => {
  const [equipmentsList, setEquipmentsList] =
    useState<FleetApiTypes.GetEquipmentsListResponse | null>(null)
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false)
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment>()
  const [showEquipmentsRequiredToFillIn, setShowEquipmentsRequiredToFillIn] =
    useState(false)
  const isOperator = useAppSelector(isOperatorSelector)
  const sortingData = useAppSelector(sortingSelector)
  const selectedFiltersCount = useAppSelector(selectedFiltersCountSelector)
  const selectedFilters = useAppSelector(selectedFiltersSelector)
  const filterItems = useAppSelector(filterItemsSelector)
  const isMigratedUser = useAppSelector(isMigratedUserSelector)

  const companyId = useCompanyId()

  const breadcrumbsList = [breadcrumbs.profileList]

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { page, pageSize, handleResetPagination } = usePagination()

  const { getCompanyInfo } = useCompanyInfo(false)

  const deleteEquipmentRequest = useApiRequest((selectedEquipment) =>
    fleetApi.deleteEquipment(selectedEquipment.groupId)
  )

  const fetchEquipmentsRequest = useApiRequest((companyId) =>
    fleetApi.getEquipmetsList(
      page - 1,
      Number(pageSize),
      Number(companyId),
      showEquipmentsRequiredToFillIn,
      prepareFiltersForReq(
        selectedFilters.equipments,
        selectedFiltersCount.equipments,
        EQUIPMENTS_FILTER_KEY
      ),
      prepareSortingForReq(sortingData.equipments, EQUIPMENTS_FILTER_KEY)
    )
  )

  const getFilterSettings = useFilterSettings(
    EQUIPMENTS_FILTER_KEY,
    handleResetPagination
  )

  const getEquipmentsFiltersRequest = useApiRequest((companyId) =>
    fleetApi.getEquipmentsFilters(companyId)
  )

  const fetchFilters = async () => {
    const getEquipmentsFilters = await getEquipmentsFiltersRequest.apiRequest(
      companyId
    )

    if (getEquipmentsFilters) {
      dispatch(
        setFilterItems({
          items: prepareFilterItems(getEquipmentsFilters.data),
          key: EQUIPMENTS_FILTER_KEY,
        })
      )
    }
  }

  useEffect(() => {
    !selectedFiltersCount.equipments && fetchFilters()
  }, [selectedFiltersCount.equipments])

  const fetchEquipments = async (companyId: string | number) => {
    const equipmentsListResponse = await fetchEquipmentsRequest.apiRequest(
      companyId
    )
    if (equipmentsListResponse) {
      setEquipmentsList(equipmentsListResponse.data)
    }
  }

  useEffect(() => {
    if (companyId) fetchEquipments(companyId)
  }, [
    page,
    pageSize,
    companyId,
    sortingData,
    selectedFiltersCount.equipments,
    showEquipmentsRequiredToFillIn,
  ])

  const handleDeleteEquipment = (equipment: Equipment) => {
    setConfirmationModalOpen(true)
    setSelectedEquipment(equipment)
  }

  const handleConfirm = async () => {
    if (selectedEquipment && companyId) {
      const response = await deleteEquipmentRequest.apiRequest(
        selectedEquipment
      )
      if (response) {
        dispatch(
          setNotificationMessage({
            notificationMessage:
              EQUIPMENT_DELETE_CONFIRMATION.SUCCESS_MODAL.TITLE,
          })
        )
        if (isMigratedUser) {
          getCompanyInfo(Number(companyId))
        }
        GTM.dataLayer({
          event: DATA_LAYER_EVENT.EQUIPMENT_REMOVED,
          equipment_name: selectedEquipment.name,
          value: selectedEquipment.count,
        })
        fetchEquipments(companyId)
        setConfirmationModalOpen(false)
        fetchFilters()
      }
    }
  }

  const handleModalClose = () => {
    setConfirmationModalOpen(false)
  }

  const handleAddEquipment = () => navigate('add')

  const handleRowClick = (id: string) =>
    navigate(`../equipments/${companyId}/${id}/edit`)

  const formattedHeadItems = useMemo(() => {
    const newHeadItems: TableHeadProps['item'][] = Object.values(
      EQUIPMENTS_HEAD_ITEMS
    ).map((header) => ({ value: header }))

    newHeadItems[EQUIPMENT_INDEX].filterSettings =
      getFilterSettings('equipment')
    newHeadItems[PRICE_INDEX].filterSettings = getFilterSettings('sum')
    newHeadItems[MAX_PRICE_INDEX].filterSettings = getFilterSettings('maxsum')

    return newHeadItems
  }, [filterItems.equipments, getFilterSettings])

  const formattedBodyItems = equipmentsList?.extras.pageItems?.map(
    (equipment) => ({
      items: [
        <Typography name="Subtitle7" className={styles.equipment}>
          {equipment.hasEmptyFields && (
            <LazyImage src={warningIcon} minWidth={20} height={20} />
          )}
          {equipment.name}
        </Typography>,
        equipment.sum,
        equipment.maxSum ?? '-',
        <TableTooltip
          title={equipment.refersToLocation}
          onClick={() => handleRowClick(equipment.groupId)}
        />,
      ],
      onClick: () => handleRowClick(equipment.groupId),
      removeRowClick: () => handleDeleteEquipment(equipment),
    })
  )

  const isEquipmentsWithEmptyFieldsExist = useMemo(
    () =>
      equipmentsList?.extras.pageItems?.some(
        (equipment) => equipment.hasEmptyFields
      ),
    [equipmentsList]
  )

  return (
    <>
      {isConfirmationModalOpen && (
        <Confirmation
          confirmModalProps={{
            question: `${EQUIPMENT_DELETE_CONFIRMATION.CONFIRMATION_MODAL.QUESTION} ${selectedEquipment?.name}?`,
            onConfirm: handleConfirm,
            onClose: handleModalClose,
            title: `${EQUIPMENT_DELETE_CONFIRMATION.CONFIRMATION_MODAL.DELETE} ${selectedEquipment?.name}?`,
          }}
        />
      )}
      <Container
        breadcrumbList={isOperator ? breadcrumbsList : []}
        title={MENU_NAMES.EQUIPMENTS}
        currentPageLabel={MENU_NAMES.EQUIPMENTS}
        tooltip={EQUIPMENTS.TOOLTIP_CONTENT}
        withNavigation={isOperator}
        loading={
          fetchEquipmentsRequest.loading && !fetchEquipmentsRequest.isLoaded
        }
        rightBlock={
          isMigratedUser &&
          isEquipmentsWithEmptyFieldsExist && (
            <Checkbox
              checked={showEquipmentsRequiredToFillIn}
              onChange={setShowEquipmentsRequiredToFillIn}
              variant="primary"
              borderVariant="grey"
              label={EQUIPMENTS.CHECKBOX_LABEL}
              size="secondary"
              typographyName="body2WMedium"
            />
          )
        }
      >
        <>
          {isEquipmentsWithEmptyFieldsExist && !isOperator && (
            <MissingFieldsBadge message={EQUIPMENTS.MISSING_FIELDS_MESSAGE} />
          )}
          <div className={styles.list}>
            <div className={styles.header}>
              <div className={styles['action-buttons']}>
                <ResetFiltersButton
                  selectedFiltersCount={selectedFiltersCount.equipments}
                />
                <div className={styles.button}>
                  <Button
                    size="small"
                    typographyName="Button2"
                    variant="outline"
                    label={EQUIPMENTS.ADD_BUTTON}
                    onClick={handleAddEquipment}
                    iconPosition="right"
                    icon={<PlusIcon color="blue700" />}
                  />
                </div>
              </div>
            </div>
            {formattedBodyItems && (
              <div className={styles.table}>
                <Table
                  headItems={formattedHeadItems}
                  bodyItems={formattedBodyItems}
                  initItemsWidth={['300', '110', '137', '306']}
                />
              </div>
            )}
          </div>
          <footer className={styles.pagination}>
            <Pagination
              defaultPage={page}
              defaultPageSize={Number(pageSize)}
              pageItemCount={equipmentsList?.extras.pageItems.length}
              total={equipmentsList?.extras.totalItems || 0}
              pageSizeLabel={PAGINATION_DROPDOWN_LABEL}
            />
          </footer>
        </>
      </Container>
    </>
  )
}
